/**
 * COMPANY-WIDE EXPERT (EXPERT-ROOMS.md §14) — on an Expert Network's Settings: share it with every workspace of the
 * company, choose how they get in (you approve each, or anyone in the company), and see / approve / revoke who uses
 * it. Subscribers get its rules read-only and ask it questions; nothing of theirs comes here but the question.
 * Shown only for an Expert Network inside a company (``network.companySharing.available``); the network's creator or
 * a workspace admin acts (``network.canChangeAccess``) — the server enforces both.
 *
 * SERVING VERSION (§20): the expert keeps ONE identity; a better network of this workspace can take over (Cut over) —
 * subscribers keep calling the same expert and only see the new version; Roll back points it at the version before.
 * A version that was cut over FROM shows who serves the expert now (its sharing is managed there).
 */
import React, { useState } from 'react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { Loader2 } from 'lucide-react';
import { confirmDialog } from '@stepflow/ui-shared/components/DialogHost.jsx';
import ExpertProdReleaseDialog from './ExpertProdReleaseDialog.jsx';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';
const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';
const STATUS = { active: 'Using it', pending: 'Waiting for you', revoked: 'Removed' };

/** THE SERVING VERSION (§20) — which network answers for this expert, Cut over to a successor, Roll back. */
function ServingVersion({ cs, canAct, busy, act, spin }) {
  const ident = cs.identity;
  const [to, setTo] = useState('');
  if (!ident) return null;
  if (!ident.isServing) {
    return (
      <p className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800" data-testid="serving-elsewhere">
        This version no longer serves the expert — <span className="font-medium">{ident.servingNetworkId}</span> does
        now. Its subscribers and sharing are managed there; this network can be deleted like any other.
      </p>
    );
  }
  const successors = cs.successors || [];
  const prev = (ident.previous || [])[0]?.networkId || '';
  if (!successors.length && !prev && !(cs.carryTargets || []).length) return null;
  const pick = to || successors[0]?.networkId || '';
  const pickName = successors.find((x) => x.networkId === pick)?.name || pick;
  const cutover = async () => {
    if (!(await confirmDialog({ title: `Cut over to ${pickName}?`,
      message: `${pickName} answers for this expert from now on. Subscribers keep the same expert and see only the new `
        + 'version; you can roll back any time.', confirmLabel: 'Cut over' }))) return;
    await act({ action: 'cutover', to: pick });
  };
  const rollback = async () => {
    if (!(await confirmDialog({ title: 'Roll back?', message: `${prev} answers for this expert again.`,
      confirmLabel: 'Roll back', danger: true }))) return;
    await act({ action: 'rollback' });
  };
  return (
    <div className="space-y-2" data-testid="serving-version">
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="text-gray-600">Serving version:</span>
      <span className="font-medium text-gray-800" data-testid="serving-version-current">{ident.servingNetworkId}</span>
      {canAct && successors.length ? (
        <>
          <select value={pick} onChange={(e) => setTo(e.target.value)} disabled={!!busy} data-testid="serving-version-pick"
            aria-label="Successor version" className="rounded-md border border-gray-200 px-2 py-1 text-xs">
            {successors.map((x) => <option key={x.networkId} value={x.networkId}>{x.name}</option>)}
          </select>
          <button type="button" disabled={!!busy || !pick} onClick={cutover} data-testid="serving-version-cutover"
            className={`${btn} bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}>
            {spin('cutover', 'Cut over', 'Cutting over…')}
          </button>
        </>
      ) : null}
      {canAct && prev ? (
        <button type="button" disabled={!!busy} onClick={rollback} data-testid="serving-version-rollback"
          className={`${btn} text-gray-700 hover:bg-gray-50`}>
          {spin('rollback', `Roll back to ${prev}`, 'Rolling back…')}
        </button>
      ) : null}
    </div>
    {canAct ? <CarryOver cs={cs} busy={busy} act={act} spin={spin} /> : null}
    </div>
  );
}

/** RETIRE with notice (§20c) — no successor: a date + a note; subscribers see it until then; the nightly sweep
 *  removes it from the registry on the date. Cancel any time before. */
function RetireRow({ cs, canAct, busy, act, spin }) {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState('');
  const [note, setNote] = useState('');
  const users = (cs.subscribers || []).filter((s) => s.status === 'active' || s.status === 'pending').length;
  if (cs.retireAt) {
    return (
      <p className="flex flex-wrap items-center gap-2 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800" data-testid="retiring">
        <span>Retiring on <span className="font-medium">{String(cs.retireAt).slice(0, 10)}</span>
          {cs.retireNote ? ` — ${cs.retireNote}` : ''}. It takes no new workspaces; the ones using it have been told.</span>
        {canAct ? (
          <button type="button" disabled={!!busy} onClick={() => act({ action: 'unretire' })} data-testid="retire-cancel"
            className={`${btn} text-amber-800 hover:bg-amber-100`}>{spin('unretire', 'Cancel retirement', 'Cancelling…')}</button>
        ) : null}
      </p>
    );
  }
  if (!canAct) return null;
  if (!open) {
    return (
      <button type="button" disabled={!!busy} onClick={() => setOpen(true)} data-testid="retire-open"
        className={`${btn} text-gray-600 hover:bg-gray-50`}>Retire…</button>
    );
  }
  const min = new Date(Date.now() + (users ? (cs.minNoticeDays || 7) : 0) * 86400000).toISOString().slice(0, 10);
  const schedule = async () => {
    if (!(await confirmDialog({ title: `Retire on ${at}?`, message: users
      ? `${users} workspace(s) use it. They're told now, keep it until ${at}, then lose it. Nobody new can subscribe.`
      : `Nobody uses it. It leaves the registry on ${at}.`, confirmLabel: 'Schedule retirement', danger: true }))) return;
    if (await act({ action: 'retire', at, note })) setOpen(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="retire-form">
      <label htmlFor="retire-at" className="text-gray-600">Retire on</label>
      <input id="retire-at" type="date" min={min} value={at} onChange={(e) => setAt(e.target.value)} data-testid="retire-at"
        className="rounded-md border border-gray-200 px-2 py-1 text-xs" />
      <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What should they use instead?"
        aria-label="Note for the workspaces that use it" data-testid="retire-note" maxLength={500}
        className="min-w-[14rem] flex-1 rounded-md border border-gray-200 px-2 py-1 text-xs" />
      <button type="button" disabled={!!busy || !at || at < min} onClick={schedule} data-testid="retire-schedule"
        className={`${btn} bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50`}>{spin('retire', 'Schedule', 'Scheduling…')}</button>
      <button type="button" onClick={() => setOpen(false)} className={`${btn} text-gray-600 hover:bg-gray-50`}>Cancel</button>
      {users ? <span className="w-full text-[11px] text-gray-500">At least {cs.minNoticeDays || 7} days&apos; notice while workspaces use it.</span> : null}
    </div>
  );
}

const KIND_WORDS = { rule: 'rule', procedure: 'procedure', declaration: 'setting', 'fact-schema': 'fact kind',
  qa: 'trained answer', skill: 'skill', 'fact-value': 'fact', document: 'document', lesson: 'lesson',
  'track-record': 'track record' };
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

/** PURE — the carry-over report (the knowledge store's apply report — KNOWLEDGE.md §11) in plain words. */
export function carrySummary(r) {
  if (!r) return '';
  const rep = r.report || {};
  const kinds = Object.keys(rep).sort();
  const proposed = rep.rule?.propose || 0;
  const came = kinds.filter((k) => (rep[k].add || 0) + (rep[k].replace || 0) > 0)
    .map((k) => plural((rep[k].add || 0) + (rep[k].replace || 0), KIND_WORDS[k] || k));
  const parts = [proposed ? `${plural(proposed, 'rule')} as suggestions to accept in its Rules tab` : '', ...came]
    .filter(Boolean);
  // the COUNT is the report's (``refused`` carries at most 20 reasons)
  const refusedN = kinds.reduce((n, k) => n + (rep[k].refuse || 0), 0);
  const unavailable = r.unavailable || [];
  const head = parts.length ? parts.join(' · ')
    : (refusedN || unavailable.length) ? 'Nothing came over' : 'Nothing new — it already has everything';
  const refused = (r.refused || []).map((a) => a.reason).filter(Boolean);
  const extra = [
    refusedN ? `${plural(refusedN, 'item')} couldn't go there${refused.length ? ` (${[...new Set(refused)].slice(0, 2).join('; ')})` : ''}` : '',
    unavailable.length ? `not checked here: ${unavailable.map((k) => KIND_WORDS[k] || k).join(', ')}` : '',
  ].filter(Boolean);
  return extra.length ? `${head}. ${extra.join('; ')}.` : `${head}.`;
}

/** KNOWLEDGE CARRY-OVER (§20b) — this (serving) version's learning into a newer one, before the cutover. */
function CarryOver({ cs, busy, act, spin }) {
  const targets = cs.carryTargets || [];
  const [to, setTo] = useState('');
  const [report, setReport] = useState(null);
  if (!targets.length) return null;
  const pick = to || targets[0].networkId;
  const t = targets.find((x) => x.networkId === pick) || targets[0];
  const run = async () => {
    const preview = await act({ action: 'carryover', to: pick, dryRun: true });
    if (!preview?.carryover) return;
    if (!(await confirmDialog({ title: `Carry knowledge over to ${t.name}?`,
      message: `${carrySummary(preview.carryover)} It goes into ${t.name}'s development version — release it to `
        + 'production before you cut over.', confirmLabel: 'Carry over' }))) return;
    const done = await act({ action: 'carryover', to: pick });
    if (done?.carryover) setReport(done.carryover);
  };
  return (
    <div className="text-xs" data-testid="carry-over">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-gray-600">Carry what it learned to:</span>
        <select value={pick} onChange={(e) => { setTo(e.target.value); setReport(null); }} disabled={!!busy}
          data-testid="carry-over-pick" aria-label="Carry knowledge to" className="rounded-md border border-gray-200 px-2 py-1 text-xs">
          {targets.map((x) => <option key={x.networkId} value={x.networkId}>{x.name}</option>)}
        </select>
        <button type="button" disabled={!!busy} onClick={run} data-testid="carry-over-run"
          className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50`}>
          {spin('carryover', 'Carry over…', 'Working…')}
        </button>
        {t.lastCarry?.at ? <span className="text-gray-400" data-testid="carry-over-last">last carried {t.lastCarry.at.slice(0, 10)}</span> : null}
      </div>
      {report ? <p className="mt-1 text-gray-600" data-testid="carry-over-report">{carrySummary(report)}</p> : null}
    </div>
  );
}

export default function CompanySharingSection({ network, onNetworkUpdate, onSubmitToRegistry = null, hideTitle = false,
  onManageSubscriptions = null }) {
  const cs = network?.companySharing;
  const [busy, setBusy] = useState('');                  // which action is running — its button shows progress
  const [err, setErr] = useState('');
  const [dialog, setDialog] = useState(false);           // Share / Edit listing — the release dialog (§18)
  const prodStage = ((network?.expertReleases || {}).stages || []).find((x) => x.stage === 'prod') || {};
  const prodRelease = ((network?.expertReleases || {}).releases || []).find((r) => r.version === prodStage.version) || null;
  if (!cs || !cs.available) return null;
  const canAct = !!network?.canChangeAccess;

  const act = async (req) => {
    setBusy(req.action + (req.tenantId ? `:${req.tenantId}` : '')); setErr('');
    try {
      const res = await fetch(`${API_BASE_URL}/networks/${encodeURIComponent(network.networkId)}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${getRestApiBearerToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ companySharing: req }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.message || `HTTP ${res.status}`);
      onNetworkUpdate?.({ ...network, companySharing: body.companySharing || body });
      return body.companySharing || body || true;
    } catch (e) {
      setErr(e.message);
      return false;
    } finally {
      setBusy('');
    }
  };
  const spin = (key, label, working) => (busy === key
    ? <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" aria-hidden />{working}</span>
    : label);

  return (
    <section className={hideTitle ? 'space-y-3' : 'bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-3'} data-testid="company-sharing">
      {hideTitle ? null : <h3 className="text-sm font-semibold text-gray-800">Release to registry</h3>}
      <p className="text-xs text-gray-500">
        Listed in the registry, your organization&apos;s workspaces can subscribe and use this expert: its rules guide
        their rooms (they can&apos;t change them) and they ask it questions here. Only the question comes to you — never
        their rooms or files.
      </p>
      <ServingVersion cs={cs} canAct={canAct} busy={busy} act={act} spin={spin} />
      {cs.identity && !cs.identity.isServing ? null : cs.published ? (
        <>
          <div className="flex items-center gap-2 text-xs" data-testid="company-sharing-on">
            <span className="rounded bg-emerald-50 px-1.5 py-px font-medium text-emerald-700">In the registry</span>
            <span className="text-gray-600">{cs.approval === 'auto' ? 'Any workspace can use it' : 'You approve each workspace'}</span>
            {canAct ? (
              <>
                <button type="button" disabled={!!busy} className={`${btn} text-indigo-700 hover:bg-indigo-50`}
                  data-testid="company-sharing-edit" onClick={() => setDialog(true)}>
                  Edit listing
                </button>
                <button type="button" disabled={!!busy} className={`${btn} text-rose-700 hover:bg-rose-50`}
                  data-testid="company-sharing-stop"
                  onClick={async () => (await confirmDialog({ title: 'Remove from the registry?', message: 'Every subscribed workspace loses it at once.',
                    confirmLabel: 'Remove', danger: true })) && act({ action: 'unpublish' })}>
                  {spin('unpublish', 'Remove from registry', 'Removing…')}
                </button>
              </>
            ) : null}
          </div>
          <RetireRow cs={cs} canAct={canAct} busy={busy} act={act} spin={spin} />
          {onManageSubscriptions ? (
            // who uses it is managed on the network's SUBSCRIPTIONS tab (§14a) — here, just where things stand
            <p className="text-xs text-gray-600" data-testid="company-sharing-subscribers-summary">
              {(cs.subscribers || []).filter((s) => s.status === 'active').length} workspace(s) use it
              {(cs.subscribers || []).some((s) => s.status === 'pending')
                ? <span className="text-amber-700"> · {(cs.subscribers || []).filter((s) => s.status === 'pending').length} waiting for you</span> : null}
              {' — '}
              <button type="button" onClick={onManageSubscriptions} data-testid="company-sharing-manage"
                className="font-medium text-indigo-700 hover:underline">Manage subscriptions</button>
            </p>
          ) : (
          <ul className="divide-y divide-gray-100 text-xs" data-testid="company-sharing-subscribers">
            {(cs.subscribers || []).length === 0 ? <li className="py-1 text-gray-400">No workspace uses it yet.</li> : null}
            {(cs.subscribers || []).map((s) => (
              <li key={s.tenantId} className="flex items-center gap-2 py-1.5" data-testid={`subscriber-${s.tenantId}`}>
                <span className="font-medium text-gray-800">{s.tenantName || s.tenantId}</span>
                <span className="text-gray-500">{STATUS[s.status] || s.status}</span>
                {canAct && s.status === 'pending' ? (
                  <button type="button" disabled={!!busy} className={`${btn} bg-blue-600 text-white hover:bg-blue-700`}
                    data-testid={`subscriber-approve-${s.tenantId}`} onClick={() => act({ action: 'approve', tenantId: s.tenantId })}>
                    {spin(`approve:${s.tenantId}`, 'Approve', 'Approving…')}
                  </button>
                ) : null}
                {canAct && s.status !== 'revoked' ? (
                  <button type="button" disabled={!!busy} className={`${btn} text-rose-700 hover:bg-rose-50`}
                    data-testid={`subscriber-revoke-${s.tenantId}`} onClick={() => act({ action: 'revoke', tenantId: s.tenantId })}>
                    {spin(`revoke:${s.tenantId}`, 'Remove', 'Removing…')}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          )}
        </>
      ) : canAct ? (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <button type="button" disabled={!!busy || !prodStage.version} className={`${btn} bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}
            data-testid="company-sharing-publish" onClick={() => setDialog(true)}>
            Release to registry
          </button>
          {!prodStage.version ? <span className="text-gray-500">Promote a release to Production first.</span> : null}
        </div>
      ) : (
        <p className="text-xs text-gray-500">Not shared. Only its creator or a workspace admin can share it.</p>
      )}
      {err ? <p className="text-xs text-rose-600">{err}</p> : null}
      <ExpertProdReleaseDialog open={dialog} inline mode="share" network={network} shared={!!cs.published}
        release={prodRelease ? { ...prodRelease, label: prodStage.label || prodRelease.label } : null}
        busy={busy === 'publish'} onCancel={() => setDialog(false)}
        onRelease={async (pub) => {
          const ok = await act({ action: 'publish', approval: pub.approval || cs.approval || 'manual', notes: pub.notes,
            summary: pub.summary, tags: pub.tags, refreshListing: true });
          if (ok) {
            setDialog(false);
            if (pub.visibility === 'registry' && prodStage.networkRelease) {
              onSubmitToRegistry?.({ releaseVersion: prodStage.networkRelease, label: prodStage.label });
            }
          }
        }} />
    </section>
  );
}
